import { join, map, or, regexp, seq, token } from '@st/util/tspc'

export type CalculatorExpression =
  | { op: AddSubtractOperator; children: CalculatorExpression[] }
  | { op: MultiplyDivideOperator; children: CalculatorExpression[] }
  | { op: 'number'; value: string }

type AddSubtractOperator = '+' | '-'
type MultiplyDivideOperator = '*' | '/'

// Helper parser that always succeeds with an empty array
// Used to make a sequence of operations optional (zeroOrMore).
const succeedEmptyArray =
  <T>() =>
  (input: string, position: number): { success: true; value: T[]; position: number } => ({
    success: true,
    value: [] as T[],
    position
  })

// Updated to use stringToCents
const numberToken = map(
  regexp(/-?([0-9]+(\.[0-9]*)?|\.[0-9]+)/),
  (s: string): CalculatorExpression => ({ op: 'number', value: s })
)

// Term handles numbers directly
const termToken = numberToken

// Factor handles multiplication and division
const mulDivOperatorToken = or<string, MultiplyDivideOperator>(token('*'), token('/'))
const mulDivOpTermPairParser = seq(mulDivOperatorToken, termToken)
const zeroOrMoreMulDivOpTermPairs = or(
  join(mulDivOpTermPairParser, token('')), // token('') as separator means pairs are just concatenated
  succeedEmptyArray<[MultiplyDivideOperator, CalculatorExpression]>()
)
const factorToken = map(
  seq(termToken, zeroOrMoreMulDivOpTermPairs),
  ([firstTerm, subsequentOpTermPairs]) => {
    if (subsequentOpTermPairs.length === 0) {
      return firstTerm
    }

    // Try to form an n-ary multiplication node
    const firstOperator = subsequentOpTermPairs[0][0]
    if (firstOperator === '*') {
      const allMultiply = subsequentOpTermPairs.every(([op, _]) => op === '*')
      if (allMultiply) {
        const children = [firstTerm]
        for (const [_op, term] of subsequentOpTermPairs) {
          children.push(term)
        }
        return { op: '*', children } as CalculatorExpression
      }
    }

    // Default to left-associative binary operations for division or mixed operations
    return subsequentOpTermPairs.reduce(
      (acc, [op, val]) => ({ op, children: [acc, val] }) as CalculatorExpression,
      firstTerm
    )
  }
)

// Expression handles addition and subtraction
const addSubOperatorToken = or<string, AddSubtractOperator>(token('+'), token('-'))
const addSubOpFactorPairParser = seq(addSubOperatorToken, factorToken)
const zeroOrMoreAddSubOpFactorPairs = or(
  join(addSubOpFactorPairParser, token('')),
  succeedEmptyArray<[AddSubtractOperator, CalculatorExpression]>()
)
const exprToken = map(
  seq(factorToken, zeroOrMoreAddSubOpFactorPairs),
  ([firstFactor, subsequentOpFactorPairs]) => {
    if (subsequentOpFactorPairs.length === 0) {
      return firstFactor
    }

    // Try to form an n-ary addition node
    const firstOperator = subsequentOpFactorPairs[0][0]
    if (firstOperator === '+') {
      const allAddition = subsequentOpFactorPairs.every(([op, _]) => op === '+')
      if (allAddition) {
        const children = [firstFactor]
        for (const [_op, factor] of subsequentOpFactorPairs) {
          children.push(factor)
        }
        return { op: '+', children } as CalculatorExpression
      }
    }

    // Default to left-associative binary operations for subtraction or mixed operations
    return subsequentOpFactorPairs.reduce(
      (acc, [op, val]) => ({ op, children: [acc, val] }) as CalculatorExpression,
      firstFactor
    )
  }
)

export const CalculatorExpression = {
  parse: (expr: string): CalculatorExpression | undefined => {
    if (expr.length === 0) return undefined
    // Remove all whitespace to simplify parsing, as operators are single characters
    const cleanedExpr = expr.replace(/\s+/g, '')
    const res = exprToken(cleanedExpr, 0)
    // Ensure the entire string was consumed
    return res.success && res.position === cleanedExpr.length ? res.value : undefined
  },
  eval: (expr: CalculatorExpression | string): string | undefined => {
    const resolvedExpr = typeof expr === 'string' ? CalculatorExpression.parse(expr) : expr
    if (!resolvedExpr) {
      return undefined
    }
    const resultInCents = evalMathExpression(resolvedExpr)

    // something went wrong for example division by zero
    if (isNaN(resultInCents)) {
      return undefined
    }
    return centsToString(resultInCents)
  },
  validate: (expr: CalculatorExpression | string): boolean => {
    return CalculatorExpression.eval(expr) !== undefined
  }
}

// Refactored evalMathExpression
function evalMathExpression(expr: CalculatorExpression): number {
  // Still returns number (cents or NaN)
  switch (expr.op) {
    case 'number':
      // Convert string value to cents when a number is directly evaluated.
      return stringToCents(expr.value)
    case '+': {
      if (expr.children.length === 0) return NaN // Should not happen with valid parsing
      let sumCents = 0
      for (const child of expr.children) {
        const childCents = evalMathExpression(child)
        if (isNaN(childCents)) return NaN
        sumCents += childCents
      }
      return sumCents
    }
    case '-': {
      // Subtraction remains binary and left-associative, parser ensures 2 children
      if (expr.children.length !== 2) return NaN
      const leftCents = evalMathExpression(expr.children[0])
      const rightCents = evalMathExpression(expr.children[1])
      if (isNaN(leftCents) || isNaN(rightCents)) return NaN
      return leftCents - rightCents
    }
    case '*': {
      if (expr.children.length === 0) return NaN // Should not happen
      const childCentsArray = expr.children.map(evalMathExpression)
      if (childCentsArray.some(isNaN)) return NaN

      let resultCents = childCentsArray[0]
      for (let i = 1; i < childCentsArray.length; i++) {
        resultCents = Math.round((resultCents * childCentsArray[i]) / 100)
      }
      return resultCents
    }
    case '/': {
      // Division remains binary and left-associative, parser ensures 2 children
      if (expr.children.length !== 2) return NaN // Should not happen
      const leftCents = evalMathExpression(expr.children[0])
      const rightCents = evalMathExpression(expr.children[1])
      if (isNaN(leftCents) || isNaN(rightCents) || rightCents === 0) {
        return NaN
      }
      return Math.round((leftCents * 100) / rightCents)
    }
  }
}

function stringToCents(amountStr: string): number {
  const parsed = parseFloat(amountStr)
  if (isNaN(parsed)) return NaN
  return Math.round(parsed * 100)
}

function centsToString(cents: number): string {
  return (cents / 100).toFixed(2)
}
